import { useLayoutEffect } from 'react'
import { useAppNavigate } from 'app-layered-layout/useAppNavigate'

declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler?: (...args: any[]) => Promise<any> | any
    }
    fromGameToDeposit?: (search: string) => void
  }
}

export const checkInWebview = () =>
  typeof window !== 'undefined' &&
  typeof window.flutter_inappwebview !== 'undefined' &&
  typeof window.flutter_inappwebview?.callHandler === 'function'

/**
 * 不依賴 React 的 handler 呼叫。
 * 只需要下載圖片之類的單次動作時請用這個，不要為此掛上 useWebViewHandler——
 * 該 hook 會在 mount 時註冊 window.fromGameToDeposit、unmount 時刪除它，
 * 短命元件（modal）掛載後再卸載會把仍在使用中的全域 callback 一併移除。
 */
export const callWebViewHandler = async (name: string, ...args: any[]) => {
  if (checkInWebview()) {
    return await window.flutter_inappwebview!.callHandler!(name, ...args)
  }
  return null
}

export const downloadImageToDevice = async (imageData: string, imageName: string) =>
  callWebViewHandler('downloadImage', { dataUrl: imageData, filename: imageName })

export const useWebViewHandler = () => {
  const navigate = useAppNavigate()

  const callHandler = async (name: string, ...args: any[]) => {
    if (checkInWebview()) {
      return await window.flutter_inappwebview!.callHandler!(name, ...args)
    }
    return null
  }

  const downloadImage = async (imageData: string, imageName: string) => {
    return await callHandler('downloadImage', {
      dataUrl: imageData,
      filename: imageName,
    })
  }

  // 開啟遊戲
  const requestOpenGame = async (path: string) => {
    const fullUrl = window.location.origin + path
    return await callHandler('openGame', fullUrl)
  }

  // 關閉遊戲
  const requestCloseGame = async () => {
    return await callHandler('closeGame')
  }

  // 開啟遊戲存款
  const requestToDeposit = async (search: string) => {
    return await callHandler('toDepositFromGame', search)
  }

  // 開啟外部連結
  const requestOpenLink = async (url: string) => {
    return await callHandler('openLink', url)
  }

  useLayoutEffect(() => {
    window.fromGameToDeposit = (search: string) => {
      navigate({
        pathname: '/game-deposit',
        search,
      })
    }

    return () => {
      try {
        // cleanup when component unmounts
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete (window as any).fromGameToDeposit
      } catch (e) {
        ;(window as any).fromGameToDeposit = undefined
      }
    }
  }, [navigate])

  return {
    checkInWebview,
    callHandler,
    downloadImage,
    requestOpenGame,
    requestCloseGame,
    requestToDeposit,
    requestOpenLink,
  }
}

export {}
